/**
 * Fluidité B2 — « Un mot, un endroit » : 6 onglets qui suivent le chantier
 * (PARCOURS-2026-09, 00-SYNTHESE « Ce qu'il y a en trop » ; P1#13, P3#6, P3#11).
 *
 *   Synthèse · Conception · Marchés · Chantier · Argent · Réception   (+ Documents)
 *
 * Chaque onglet regroupe des « vues » (les anciens onglets, dont les droits restent
 * calculés dans pages/ChantierDetail.jsx › TABS_BY_ROLE_PROJET) :
 *  - Chantier : Réunions, Comptes rendus, Actions, Visas, Plans, Galerie, Planning ;
 *  - Argent   : Finances, Paiements (client), Mes situations (entreprise), Point MOA.
 *
 * Les anciens liens (`?tab=suivi&sub=crc`, `?tab=taches`, `?tab=finances`,
 * `?tab=point-moa`…) envoyés dans les e-mails et les notifications restent valables :
 * `resoudreOnglet` les redirige vers le bon onglet et la bonne vue.
 */
import { etapeChantier, ONGLETS_CHANTIER_LIBELLES as O, VUES_CHANTIER_LIBELLES as V } from './libelles';

// Libellés des 6 onglets (+ Documents) et de leurs vues.
export const ONGLETS_CHANTIER = [
  { id: 'synthese', nom: O.synthese, vues: ['synthese'] },
  { id: 'conception', nom: O.conception, vues: ['conception'] },
  { id: 'marches', nom: O.marches, vues: ['marches'] },
  { id: 'chantier', nom: O.chantier, vues: ['reunions', 'crc', 'actions', 'visas', 'plans', 'galerie', 'planning'] },
  { id: 'argent', nom: O.argent, vues: ['finances', 'paiements', 'situations', 'point-moa'] },
  { id: 'reception', nom: O.reception, vues: ['reception'] },
  // Documents : accessible de partout (dernier onglet, à part).
  { id: 'documents', nom: O.documents, vues: ['documents'] },
];

export const VUES_CHANTIER = {
  synthese: { nom: O.synthese, droit: 'overview' },
  conception: { nom: O.conception, droit: 'conception' },
  marches: { nom: O.marches, droit: 'marches' },
  reunions: { nom: V.reunions, droit: 'suivi', suivi: 'reunions' },
  crc: { nom: V.crc, droit: 'suivi', suivi: 'crc' },
  actions: { nom: V.actions, droit: 'taches' },
  visas: { nom: V.visas, droit: 'visas' },
  plans: { nom: V.plans, droit: 'plans' },
  galerie: { nom: V.galerie, droit: 'galerie' },
  planning: { nom: V.planning, droit: 'suivi', suivi: 'planning' },
  finances: { nom: V.finances, droit: 'finances' },
  paiements: { nom: V.paiements, droit: 'paiements' },
  situations: { nom: V.situations, droit: 'situations' },
  'point-moa': { nom: V['point-moa'], droit: 'point-moa' },
  reception: { nom: O.reception, droit: 'reception' },
  documents: { nom: O.documents, droit: 'documents' },
};

/** Onglet qui contient une vue. */
export const ongletDeLaVue = (vue) => (ONGLETS_CHANTIER.find((o) => o.vues.includes(vue)) || ONGLETS_CHANTIER[0]).id;

// Anciens identifiants d'onglet (liens des e-mails, notifications, tableaux de bord)
// → vue actuelle. `sub` (ancien sous-onglet de Suivi chantier) est pris en compte à part.
const ANCIENS_ONGLETS = {
  overview: 'synthese',
  synthese: 'synthese',
  suivi: null, // dépend de `sub`
  planning: 'reunions', // RP13 — ancien onglet « Agenda »
  convocations: 'reunions',
  taches: 'actions',
  'mes-taches': 'actions',
  actions: 'actions',
  galerie: 'galerie',
  visas: 'visas',
  plans: 'plans',
  finances: 'finances',
  paiements: 'paiements',
  situations: 'situations',
  'point-moa': 'point-moa',
  argent: null, // premier écran visible de l'onglet
  chantier: null,
};

const SOUS_ONGLETS_SUIVI = { crc: 'crc', reunions: 'reunions', planning: 'planning', actions: 'actions', taches: 'actions' };

/**
 * Lien (ancien ou nouveau) → { onglet, vue }. `vue` vaut null quand l'onglet doit
 * ouvrir sa première vue visible.
 *   ?tab=suivi&sub=crc     → chantier / crc
 *   ?tab=suivi             → chantier / (première vue)
 *   ?tab=taches&etat=…     → chantier / actions
 *   ?tab=finances          → argent / finances
 *   ?tab=point-moa         → argent / point-moa
 *   ?tab=overview          → synthese
 *   ?tab=chantier&sub=crc  → chantier / crc
 */
export function resoudreOnglet(tab, sub, extras = {}) {
  const t = String(tab || '').trim();
  const s = String(sub || '').trim();
  if (!t) return { onglet: 'synthese', vue: 'synthese' };
  // Nouveaux liens : ?tab=<onglet>&sub=<vue>
  const onglet = ONGLETS_CHANTIER.find((o) => o.id === t);
  if (onglet && t !== 'reception' && t !== 'documents' && onglet.vues.length > 1) {
    const vue = onglet.vues.includes(s) ? s : (SOUS_ONGLETS_SUIVI[s] && onglet.vues.includes(SOUS_ONGLETS_SUIVI[s]) ? SOUS_ONGLETS_SUIVI[s] : null);
    return { onglet: onglet.id, vue };
  }
  if (t === 'suivi') {
    if (SOUS_ONGLETS_SUIVI[s]) return { onglet: 'chantier', vue: SOUS_ONGLETS_SUIVI[s] };
    // ?tab=suivi&element_id=… ou &taches=… : une action précise
    if (extras.element_id || extras.taches) return { onglet: 'chantier', vue: 'actions' };
    if (extras.action === 'create-crc') return { onglet: 'chantier', vue: 'crc' };
    return { onglet: 'chantier', vue: null };
  }
  if (Object.prototype.hasOwnProperty.call(ANCIENS_ONGLETS, t) && ANCIENS_ONGLETS[t]) {
    const vue = ANCIENS_ONGLETS[t];
    return { onglet: ongletDeLaVue(vue), vue };
  }
  if (VUES_CHANTIER[t]) return { onglet: ongletDeLaVue(t), vue: t };
  // Onglet inconnu (ancien « messages », « visites »…) : la Synthèse.
  return { onglet: 'synthese', vue: 'synthese' };
}

// Selon l'étape du chantier, les onglets hors sujet restent accessibles mais sont
// affichés en retrait (texte plus clair). Rien n'est masqué pour autant.
export const ONGLETS_UTILES_PAR_ETAPE = {
  etudes: ['synthese', 'conception', 'documents'],
  consultation: ['synthese', 'conception', 'marches', 'documents'],
  chantier: ['synthese', 'marches', 'chantier', 'argent', 'documents'],
  reception: ['synthese', 'chantier', 'argent', 'reception', 'documents'],
  gpa: ['synthese', 'argent', 'reception', 'documents'],
};

/**
 * Étape du chantier : le champ `etape` s'il existe (lot B1 — une seule étape
 * d'avancement), sinon la phase puis le statut (libelles.etapeChantier).
 */
export const etapeDuChantier = (chantier) => {
  const e = chantier && chantier.etape;
  if (e && ONGLETS_UTILES_PAR_ETAPE[e]) return e;
  return etapeChantier(chantier);
};

export const ongletHorsEtape = (ongletId, chantier) => {
  const utiles = ONGLETS_UTILES_PAR_ETAPE[etapeDuChantier(chantier)];
  return !!utiles && !utiles.includes(ongletId);
};
